// The one dropdown of the UI (instead of a native <select>, whose look and focus ring differ per browser): a pill
// button with a pixel caret, and a list that opens under it, over everything else (it never pushes the page down).
// A pick applies at once; the list closes on a pick, a tap or click outside, Esc or Tab. Keyboard: on the button
// ↓ / ↑ / Enter / Space open it; in the list ↑ ↓ Home End move, Enter / Space pick, Esc closes. Keys in the list never
// reach the game (no walking, no Enter to board, no Space to talk).
//   const dd = dropdown({ label: '当前楼层', options: [{ value: 1, text: '1F' }, ...], value: 1, onChange: (v) => ... });
//   parent.append(dd.el);   dd.set(v)   dd.setOptions(list, v?)   dd.close()
// Options: {value, text, icon? (public/icons name), hint? (a small second line), disabled?}. o.render(option, button)
// draws the button (default: the chosen option's icon and text, then the caret). o.className goes on the button:
// "dd-pill" for the top bar's look (as 在线 / 设置). o.doc: the document the list opens in, if not this page's
// (the 小窗, public/pip.js: a Picture-in-Picture window of the same page).
import { icon } from '/icons.js';
import { t } from '/i18n.js';

const CSS = `
.dd-btn{display:inline-flex;align-items:center;gap:6px;cursor:pointer;color:#1b1410;max-width:100%;min-width:0;text-align:left}
.dd-btn .dd-text{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.dd-btn .dd-caret{margin-left:auto}
.dd-btn[aria-expanded=true] .dd-caret{transform:scaleY(-1)}
.dd-btn:focus{outline:none}.dd-btn:focus-visible{outline:2px dashed #1b1410;outline-offset:2px}
.dd-pill{box-sizing:border-box;height:30px;background:#fff8e7;border:2px solid #1b1410;border-radius:10px;padding:0 8px 0 10px;font:12px/1.5 var(--ui-font,-apple-system,system-ui,sans-serif)}
.dd-pill[aria-expanded=true]{background:#FDD03B}
.dd-field{box-sizing:border-box;width:100%;background:#fff;border:2px solid #1b1410;border-radius:8px;padding:4px 6px 4px 8px;font:inherit;font-size:13px}
.dd-list{position:fixed;z-index:100;box-sizing:border-box;min-width:120px;max-width:calc(100vw - 16px);overflow:auto;background:#fff8e7;border:2px solid #1b1410;border-radius:10px;box-shadow:0 3px 0 #1b1410;padding:4px;font:13px/1.4 var(--ui-font,-apple-system,system-ui,sans-serif);color:#1b1410;outline:none;overscroll-behavior:contain}
.dd-opt{display:flex;align-items:center;gap:6px;padding:6px 8px;border-radius:7px;cursor:pointer;user-select:none;-webkit-user-select:none}
.dd-opt .dd-t{flex:1;min-width:0}
.dd-opt .dd-t span{display:block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.dd-opt .dd-h{font-size:11px;color:#6b5a44;white-space:normal}
.dd-opt .dd-ok{visibility:hidden}.dd-opt[aria-selected=true] .dd-ok{visibility:visible}
.dd-opt.act{background:#fde68a}
.dd-opt[aria-selected=true]{font-weight:600}
.dd-opt[aria-disabled=true]{opacity:.45;cursor:default}
@media (pointer:coarse){.dd-opt{min-height:40px;padding:8px 10px}}`;
function css(D) { if (D.getElementById('dd-css')) return; const s = D.createElement('style'); s.id = 'dd-css'; s.textContent = CSS; D.head.append(s); }
let uid = 0;

/** @returns {{el: HTMLButtonElement, set(value), setOptions(options, value?), close(), value()}} */
export function dropdown(o) {
  const D = o.doc ?? document, W = D.defaultView ?? window; // where the list opens, and its window
  css(D);
  const id = 'dd' + ++uid;
  let options = o.options ?? [], value = o.value, list = null, act = -1, byKey = false, watch = 0;
  const btn = document.createElement('button'); btn.type = 'button';
  btn.className = 'dd-btn' + (o.className ? ' ' + o.className : '');
  btn.setAttribute('aria-haspopup', 'listbox'); btn.setAttribute('aria-expanded', 'false');
  if (o.label) btn.title = o.title ?? o.label;

  const chosen = () => options.find((x) => x.value === value);
  function draw() {
    const c = chosen();
    if (o.label) btn.setAttribute('aria-label', c ? t('app.dropdown.value', { label: o.label, value: c.text }) : o.label); // the value is read out, as a <select>'s is
    if (o.render) { o.render(c, btn); return; }
    const txt = document.createElement('span'); txt.className = 'dd-text'; txt.textContent = c?.text ?? '';
    const caret = icon('caret_down'); caret.classList.add('dd-caret');
    btn.replaceChildren(...(c?.icon ? [icon(c.icon)] : []), txt, caret);
  }
  draw();

  const items = () => (list ? [...list.children] : []);
  function mark(i, scroll = true) {
    const els = items(); act = i;
    els.forEach((e, k) => e.classList.toggle('act', k === i));
    if (els[i]) { list.setAttribute('aria-activedescendant', els[i].id); if (scroll) els[i].scrollIntoView({ block: 'nearest' }); }
  }
  function step(from, d) {
    for (let k = 1; k <= options.length; k++) { const i = (from + d * k + options.length * 2) % options.length; if (!options[i].disabled) return i; }
    return from;
  }
  /** The button went away (its panel redrawn or hidden by code, not by a tap): the list goes too. */
  const gone = () => !btn.isConnected || !btn.getClientRects().length;
  function place() {
    if (!list) return;
    if (gone()) { close(); return; }
    const r = btn.getBoundingClientRect(), vw = W.innerWidth, vh = W.innerHeight;
    list.style.minWidth = Math.max(120, r.width) + 'px';
    const below = vh - r.bottom - 8, above = r.top - 8, up = below < 160 && above > below;
    list.style.maxHeight = Math.max(80, (up ? above : below) - 4) + 'px';
    const w = list.offsetWidth, h = list.offsetHeight;
    list.style.left = Math.max(8, Math.min(r.left, vw - w - 8)) + 'px';
    list.style.top = (up ? r.top - 4 - h : r.bottom + 4) + 'px';
  }
  /** The rows, for the options as they are now (on open, and when they change while open). */
  function fill() {
    list.replaceChildren(...options.map((x, i) => {
      const e = document.createElement('div'); e.className = 'dd-opt'; e.id = `${id}-${i}`; e.setAttribute('role', 'option');
      e.setAttribute('aria-selected', String(x.value === value)); if (x.disabled) e.setAttribute('aria-disabled', 'true');
      const col = document.createElement('div'); col.className = 'dd-t';
      const s = document.createElement('span'); s.textContent = x.text; col.append(s);
      if (x.hint) { const h = document.createElement('span'); h.className = 'dd-h'; h.textContent = x.hint; col.append(h); }
      const ok = icon('check'); ok.classList.add('dd-ok');
      e.append(...(x.icon ? [icon(x.icon)] : []), col, ok);
      e.addEventListener('pointermove', () => { if (!x.disabled && act !== i) mark(i, false); });
      e.addEventListener('click', () => pick(i));
      return e;
    }));
  }
  function open(keyboard) {
    if (list || btn.disabled || !options.length) return;
    byKey = keyboard;
    list = document.createElement('div'); list.className = 'dd-list'; list.id = id + '-list';
    list.setAttribute('role', 'listbox'); list.tabIndex = -1; if (o.label) list.setAttribute('aria-label', o.label);
    fill();
    list.addEventListener('keydown', onListKey);
    list.addEventListener('keyup', (e) => e.stopPropagation());
    D.body.append(list);
    btn.setAttribute('aria-expanded', 'true'); btn.setAttribute('aria-controls', list.id);
    place();
    const cur = options.findIndex((x) => x.value === value);
    mark(cur >= 0 ? cur : step(-1, 1));
    list.focus({ preventScroll: true });
    W.addEventListener('pointerdown', outside, true);
    W.addEventListener('resize', close);
    W.addEventListener('scroll', onScroll, true);
    watch = setInterval(() => { if (gone()) close(); }, 400);
  }
  function close(refocus) {
    if (!list) return;
    W.removeEventListener('pointerdown', outside, true); W.removeEventListener('resize', close); W.removeEventListener('scroll', onScroll, true);
    clearInterval(watch);
    const had = list.contains(D.activeElement);
    list.remove(); list = null; act = -1;
    btn.setAttribute('aria-expanded', 'false'); btn.removeAttribute('aria-controls');
    if (had && !gone() && (refocus === true || byKey)) btn.focus({ preventScroll: true });
    else if (had) D.activeElement?.blur();
  }
  function pick(i) {
    const x = options[i]; if (!x || x.disabled) return;
    const changed = x.value !== value;
    value = x.value; draw(); close();
    if (changed || o.always) o.onChange?.(x.value, x);
  }
  const outside = (e) => { if (list && !list.contains(e.target) && !btn.contains(e.target)) close(); };
  const onScroll = (e) => { if (list && !list.contains(e.target)) place(); };
  function onListKey(e) {
    e.stopPropagation(); // the game's keys (walk, Enter to board, Space to talk) stay out of the list
    if (e.isComposing) return;
    byKey = true; // used the keyboard: focus goes back to the button when it closes
    const k = e.key;
    if (gone()) { close(); return; }
    if (k === 'ArrowDown' || k === 'ArrowUp') { e.preventDefault(); mark(step(act, k === 'ArrowDown' ? 1 : -1)); }
    else if (k === 'Home') { e.preventDefault(); mark(step(-1, 1)); }
    else if (k === 'End') { e.preventDefault(); mark(step(options.length, -1)); }
    else if (k === 'Enter' || k === ' ') { e.preventDefault(); pick(act); }
    else if (k === 'Escape') { e.preventDefault(); close(true); }
    else if (k === 'Tab') close(true); // focus back on the button, and Tab goes on from there
  }
  btn.addEventListener('click', () => (list ? close() : open(btn.matches(':focus-visible') && !btn.dataset.ddPtr)));
  btn.addEventListener('pointerdown', () => { btn.dataset.ddPtr = '1'; setTimeout(() => delete btn.dataset.ddPtr, 600); });
  btn.addEventListener('keydown', (e) => {
    if (['ArrowDown', 'ArrowUp'].includes(e.key) || (!list && (e.key === 'Enter' || e.key === ' '))) {
      e.preventDefault(); e.stopPropagation(); open(true);
    } else if (e.key === 'Escape' && list) { e.stopPropagation(); close(true); }
  });
  // Space/Enter on a focused button also "click" on keyup in some browsers: handled above, so keep the keyup out
  btn.addEventListener('keyup', (e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); } });

  return {
    el: btn,
    value: () => value,
    set(v) { value = v; draw(); for (const [i, e] of items().entries()) e.setAttribute('aria-selected', String(options[i]?.value === value)); },
    /** New options (a mic plugged in): an open list is redrawn in place, keeping the highlighted row; it closes only
     *  if nothing is left to pick. */
    setOptions(next, v = value) {
      const was = options[act]?.value;
      options = next ?? []; value = v; draw();
      if (!list) return;
      if (!options.length) { close(); return; }
      fill(); place();
      const i = options.findIndex((x) => x.value === was);
      mark(i >= 0 && !options[i].disabled ? i : step(-1, 1), false);
    },
    close,
  };
}
